import {useState, useEffect, memo, useCallback, useRef} from 'react';
import {
  InputBase,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Stack,
  useTheme,
  Box,
  List,
  Pagination,
  Tabs,
  Tab,
  Typography,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Iconify from 'src/minimal-components/iconify';
import {useTranslation} from 'react-i18next';
import {RHFAutocomplete} from 'src/minimal-components/hook-form';
import FormConstants from '@shared/constants/FormConstants';
import {
  createAuditLog,
  getDocumentSearchResults,
  getLocalSearchResults,
  getSubmoduleDropDownList
} from '@shared/services/service-handlers/PrivateService';
import {LoadingButton} from '@mui/lab';
import Dialog, {dialogClasses} from '@mui/material/Dialog';
import Scrollbar from '@components/scrollbar';
import SearchNotFound from 'src/minimal-components/search-not-found/search-not-found';
import ResultItem from './result-item';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import {useBoolean} from 'src/hooks/use-boolean';
import {closeSnackbar, useSnackbar} from 'notistack';
import {PulseLoader} from 'react-spinners';
import {useNavigate} from 'react-router-dom';
import {
  BASE_LEGACY_APP,
  BASE_MENU_ITEM,
  BASE_RECORD_ID,
  BASE_ROUTE,
  BASE_ROUTE_MODULE,
  BASE_ROUTE_SUB_MODULE,
  BASE_SPECIMEN_NUMBER,
  BASE_SPECIMEN_NUMBER_ID,
  BASE_SPECIMEN_TYPE,
  AI_BLOB_SEARCH,
  BASE_UNIQUE_ID,
  IS_FROM_EPIC,
  IS_FROM_ATHENA,
  MODE,
  PARAMS_KEY,
  LOCAL_SEARCH,
  MOVE_TO_TAB,
  SEARCH_CONTEXT,
  CHILD_TAB_NAME,
  TRIGGER,
  BASE_ACCESSION_NUMBER
} from 'src/routes/BaseRoute';
import EncryptUtils from 'src/utils/EncryptUtils';
import {environment} from '@shared/config/environment';
import {useAppContext} from '@context/AppContext';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import AuthUtils from 'src/utils/AuthUtils';
import {useModuleContext} from '@context/ModuleContext';

function Searchbar(props: any): React.ReactElement {
  const {module_name, sub_module, record_id, ParentLegacyValue} = props;
  const {globalSearchConfig} = environment;
  const {
    appState: {applicationName}
  } = useAppContext();
  const {moduleState} = useModuleContext();
  const {isBH, isBHReason}: any = moduleState;
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const {t} = useTranslation();
  const openModal = useBoolean();
  const theme = useTheme();

  const ModuleUrl = window.location.href.split('/')[4];

  const moduleMatch = globalSearchConfig?.aiSearchModules?.find(
    (config: any) => config.module_name === module_name
  );

  const NavigateTo = moduleMatch?.navigateTo;
  const enableAISearch = moduleMatch?.enableAISearch;
  const enableDiscreteSearch = moduleMatch?.enableDiscreteSearch;
  const moduleLocation = moduleMatch?.location;
  const showDropdown = moduleMatch?.showDropdown;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLabels, setSelectedLabels] = useState<any>([]);
  const [dropdownValues, setDropdownValues] = useState<{label: string}[]>([]);
  const [autocompleteValue, setAutocompleteValue] = useState<any[]>([]);
  const [searchData, setSearchData] = useState<any[]>([]);
  const [documentsData, setDocumentsData] = useState<any[]>([]);
  const [toastPath, setToastPath] = useState('');
  const [prevSearchQuery, setPrevSearchQuery] = useState<string>('');
  const [prevDocSearchQuery, setPrevDocSearchQuery] = useState<string>('');
  const [prevSelectedLabels, setPrevSelectedLabels] = useState<string[]>([]);
  const [prevDocSelectedLabels, setPrevDocSelectedLabels] = useState<string[]>(
    []
  );
  const [noDoc, setNoDoc] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [value, setValue] = useState(enableDiscreteSearch ? 0 : 1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isRestricted, setisRestricted] = useState(false);

  const pageSize = 10;

  const containsDocuments = selectedLabels?.includes('Documents');
  const shouldCallAISearch = containsDocuments || selectedLabels.length === 0;

  const recordId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );

  const legacyApp = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_LEGACY_APP
  );

  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_UNIQUE_ID
  );

  const isFromEpic = EncryptUtils.getDecryptedData(
    location?.search,
    IS_FROM_EPIC
  );

  const isFromAthena = EncryptUtils.getDecryptedData(
    location?.search,
    IS_FROM_ATHENA
  );

  const mode = EncryptUtils.getDecryptedData(location?.search, MODE);

  const menuItem = new URLSearchParams(location?.search).get(BASE_MENU_ITEM);

  const specimenNumber = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_SPECIMEN_NUMBER
  );

  const specimenNumberId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_SPECIMEN_NUMBER_ID
  );
  const accessionNumber = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_ACCESSION_NUMBER
  );
  const specimenType = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_SPECIMEN_TYPE
  );

  const localSearch = EncryptUtils.getDecryptedData(
    location?.search,
    LOCAL_SEARCH
  );

  const aiBlobSearch = EncryptUtils.getDecryptedData(
    location?.search,
    AI_BLOB_SEARCH
  );

  const checkisbbhvalue = localStorage.getItem('check_isbh');

  const notFound = searchQuery && !searchData.length;
  const totalPages = Math.ceil(searchData.length / pageSize);

  const docNotFound = searchQuery && !documentsData.length;
  const docTotalPages = Math.ceil(documentsData.length / pageSize);

  const parsedLocalSearch = localSearch ? JSON.parse(localSearch) : {};
  const parsedAiBlobSearch = aiBlobSearch ? JSON.parse(aiBlobSearch) : {};

  const fetchDropdownValues = async () => {
    try {
      const response = await getSubmoduleDropDownList(
        applicationName,
        module_name,
        FormConstants.GLOBAL_SEARCH.local_search
      );
      setDropdownValues(response.data);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  useEffect(() => {
    if (checkisbbhvalue) {
      if (checkisbbhvalue === 'Without_Restricted') {
        setisRestricted(false);
      } else {
        setisRestricted(true);
      }
    }
  }, [checkisbbhvalue]);

  useEffect(() => {
    const storedSearchQuery = sessionStorage.getItem('@searchQuery');
    const storedToastPath = sessionStorage.getItem('@toastPath');

    if (storedSearchQuery) {
      setSearchQuery(storedSearchQuery);
    }
    if (storedToastPath) {
      setToastPath(storedToastPath);
    }
    setPrevSearchQuery('');
    setPrevDocSearchQuery('');
    if (showDropdown) {
      fetchDropdownValues();
    }
  }, []);
  const prevModuleNameRef = useRef();

  useEffect(() => {
    sessionStorage.setItem('@searchQuery', searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    // if (prevModuleNameRef.current && prevModuleNameRef.current !== module_name) {
    setSearchQuery('');
    setSelectedLabels([]);
    setAutocompleteValue([]);
    setSearchData([]);
    setDocumentsData([]);
    setToastPath('');
    setPrevSearchQuery('');
    setPrevDocSearchQuery('');
    setPrevSelectedLabels([]);
    setPrevDocSelectedLabels([]);
    sessionStorage.removeItem('@searchQuery');
    sessionStorage.removeItem('@toastPath');
    // }
    prevModuleNameRef.current = module_name;
  }, [module_name]);

  useEffect(() => {
    sessionStorage.setItem('@toastPath', toastPath);
  }, [toastPath]);

  useEffect(() => {
    // Reset states when ParentLegacyValue changes
    setSearchData([]);
    setDocumentsData([]);
    setToastPath('');
    setPrevSearchQuery('');
    setPrevDocSearchQuery('');
    setPrevSelectedLabels([]);
    setPrevDocSelectedLabels([]);
    setNoDoc(false);
    setIsLoading(false);
    setValue(enableDiscreteSearch ? 0 : 1);
    setCurrentPage(1);
  }, [ParentLegacyValue]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setSearchQuery(event.target.value);
      setCurrentPage(1);
      setToastPath('');
    },
    [setSearchQuery]
  );

  const handleToastPath = () => {
    if (!toastPath) return;

    enqueueSnackbar(toastPath, {
      variant: 'info',
      autoHideDuration: 30000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center'
      },
      style: {
        marginTop: '42px',
        marginLeft: '40px'
      },
      action: snackbarId => (
        <IconButton
          aria-label="close"
          onClick={() => closeSnackbar(snackbarId)}
          size="small"
          sx={{color: 'white'}} // Optional: make the cross white
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )
    });
  };

  const arraysEqual = (a: any[], b: any[]) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const logSearchResults = (logData: any) => {
    const dataObj: any = {...logData};

    if (dataObj.PatientID) {
      delete dataObj.PatientID;
    }

    const keyValueString = Object.entries(dataObj)
      .map(([key, value]) => {
        if (value) return `${key}: ${JSON.stringify(value)}`;
        return null;
      })
      .filter(Boolean)
      .join(', ');

    const data = {
      module: module_name,
      sub_module: 'Global Search',
      sub_module_identifier: keyValueString,
      application_name: applicationName,
      isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
      isBH_reason: isBHReason,
      event_description: `Global Search Query -> ${keyValueString}`,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp()
    };
    createAuditLog(module_name, data);
  };

  const handleSearch = async (direction = false) => {
    if (
      prevSearchQuery === searchQuery &&
      arraysEqual(prevSelectedLabels, selectedLabels)
    ) {
      if (direction) return;
      openModal.onToggle();
      return;
    }
    setIsLoading(true);
    if (!direction) {
      openModal.onToggle();
    }
    if (!searchQuery) {
      return setIsLoading(false);
    }
    try {
      const data = {
        PatientID: record_id,
        specimenNumber,
        searchQuery: searchQuery?.trim()?.toLowerCase(),
        selectedLabels,
        ParentLegacyValue
      };
      const encryptRequestData: any = EncryptUtils.encryptedData(data);

      const response = await getLocalSearchResults(
        applicationName,
        module_name,
        FormConstants.GLOBAL_SEARCH.local_search,
        {
          tk: encryptRequestData
        }
      );
      logSearchResults(data);
      setSearchData(response.data);
      setIsLoading(false);
      setPrevSearchQuery(searchQuery);
      setPrevSelectedLabels(selectedLabels);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoading(false);
    }
  };

  const handleDocSearch = async (direction = false) => {
    setNoDoc(false);
    if (
      (selectedLabels.length > 0 && !containsDocuments) ||
      (prevDocSearchQuery === searchQuery &&
        arraysEqual(prevDocSelectedLabels, selectedLabels))
    ) {
      if (selectedLabels.length > 0 && !containsDocuments) {
        setNoDoc(true);
      }
      if (direction) return;
      openModal.onToggle();
      return;
    }
    setIsLoading(true);
    if (!direction) {
      openModal.onToggle();
    }
    if (!searchQuery) {
      return setIsLoading(false);
    }
    setDocumentsData([]);
    try {
      const data = {
        PatientID: record_id,
        specimenNumber,
        searchQuery: searchQuery?.trim()?.toLowerCase(),
        ParentLegacyValue
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);

      const response = await getDocumentSearchResults(
        applicationName,
        module_name,
        FormConstants.GLOBAL_SEARCH.ai_search,
        {
          tk: encryptRequestData
        }
      );
      const allDocuments = response?.documents || [];
      const errorMessages = response?.message || '';

      setDocumentsData(allDocuments);

      if (errorMessages) {
        enqueueSnackbar(errorMessages, SnackBarConfig.getWarning());
      }

      setIsLoading(false);
      setPrevDocSearchQuery(searchQuery);
      setPrevDocSelectedLabels(selectedLabels);
      setNoDoc(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoading(false);
    }
  };

  const handleLabelToggle = (label: string) => {
    let updatedSelectedLabels: string[];

    if (selectedLabels.includes(label)) {
      updatedSelectedLabels = selectedLabels.filter(
        (item: any) => item !== label
      );
    } else {
      updatedSelectedLabels = [...selectedLabels, label];
    }

    setSelectedLabels(updatedSelectedLabels);
    setAutocompleteValue(updatedSelectedLabels);
  };

  const handleClose = useCallback(() => {
    openModal.onToggle();
  }, [openModal]);

  const onDiscreteChange = (
    path: string,
    item: any,
    isBhadded?: boolean,
    searchQuery?: string
  ) => {
    setToastPath(path);
    openModal.onToggle();

    const to = item?.SubModule;
    const data = {
      LegacyApp: item?.App,
      flowsheet_group: item?.FlowsheetGroup,
      start_date: item?.StartDate,
      stop_date: item?.StopDate
    };

    const localSearch = JSON.stringify({
      is_local_search: true,
      ID: item?.ID,
      ISBhdataFilter: isBhadded,
      submodule: item?.SubModule,
      Level2: item.Level2,
      Level2ID: item.Level2ID,
      ...(module_name.toLowerCase() === FormConstants.MODULE.LIS &&
        to.toLowerCase() === FormConstants.SUB_MODULE.RESULTS && {
          SpecimenNumber: item.SpecimenNumber
        }),
      ...(to.toLowerCase() === FormConstants.SUB_MODULE.EXAM_PROCEDURE_LIST && {
        submodule: true,
        data: {
          row: item
        }
      }),
      ...((to === FormConstants.SCREENS.FLOWSHEETS ||
        to === FormConstants.SCREENS.VITAL) && {data: data})
    });

    const paramsKey = EncryptUtils.setEncryptedParams({
      [BASE_RECORD_ID]: recordId,
      [BASE_LEGACY_APP]: legacyApp,
      [BASE_UNIQUE_ID]: uniqueId,
      [IS_FROM_EPIC]: isFromEpic,
      [IS_FROM_ATHENA]: isFromAthena,
      [MODE]: mode,
      [BASE_SPECIMEN_NUMBER]: specimenNumber,
      [BASE_SPECIMEN_NUMBER_ID]: specimenNumberId,
      [BASE_ACCESSION_NUMBER]:accessionNumber,
      [BASE_SPECIMEN_TYPE]: specimenType,
      [LOCAL_SEARCH]: localSearch,
      [MOVE_TO_TAB]: item?.Level2?.toLowerCase(),
      [SEARCH_CONTEXT]: searchQuery
    });

    let url = `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${module_name}/${BASE_ROUTE_SUB_MODULE}/${to}?${PARAMS_KEY}=${paramsKey}`;

    if (menuItem) {
      url += `&${BASE_MENU_ITEM}=${menuItem}`;
    }
    navigate(url);
  };

  const onDocChange = (
    to: any,
    filePath: any,
    searchQuery?: string,
    docItem?: any
  ) => {
    openModal.onToggle();

    const aiBlobSearch = JSON.stringify({
      is_ai_blob_search: true,
      filePath
    });

    const localSearch =
      module_name === FormConstants.MODULE.RIS
        ? JSON.stringify({
            submodule: true,
            data: {
              row: {
                App: docItem?.legacyapp,
                MRN: docItem?.legacymrn,
                AccessionNumber: docItem?.AccessionNumber,
                ProcedureType: docItem?.ProcedureType
              }
            }
          })
        : undefined;

    const paramsKey = EncryptUtils.setEncryptedParams({
      [BASE_RECORD_ID]: recordId,
      [BASE_LEGACY_APP]: legacyApp,
      [BASE_UNIQUE_ID]: uniqueId,
      [IS_FROM_EPIC]: isFromEpic,
      [IS_FROM_ATHENA]: isFromAthena,
      [MODE]: mode,
      [BASE_SPECIMEN_NUMBER]: specimenNumber,
      [BASE_SPECIMEN_NUMBER_ID]: specimenNumberId,
      [BASE_ACCESSION_NUMBER]:accessionNumber,
      [BASE_SPECIMEN_TYPE]: specimenType,
      ...(localSearch && {[LOCAL_SEARCH]: localSearch}),
      [AI_BLOB_SEARCH]: aiBlobSearch,
      ...(() => {
        if (!docItem || typeof docItem?.Path !== 'string') {
          return {};
        }

        const [topTab, subTab] = docItem.Path.split('->').map((s: string) =>
          s?.trim()
        );

        return {
          [MOVE_TO_TAB]: topTab,
          ...(subTab && {[CHILD_TAB_NAME]: subTab})
        };
      })(),
      [SEARCH_CONTEXT]: searchQuery
    });
    let url;

    const timestamp = new Date().getTime();

    if (moduleLocation === ModuleUrl) {
      url = `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${module_name}?${PARAMS_KEY}=${paramsKey}`;
    } else {
      url = `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${module_name}/${BASE_ROUTE_SUB_MODULE}/${to}?${PARAMS_KEY}=${paramsKey}&${TRIGGER}=${timestamp}`;

      if (menuItem) {
        url += `&${BASE_MENU_ITEM}=${menuItem}`;
      }
    }
    navigate(url);
  };

  const handleChangePage = (event: any, newPage: any) => {
    setCurrentPage(newPage);
  };

  const renderNonDocumentItems = () => {
    const data = searchData.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );

    function createPath(...segments: any) {
      const validSegments = segments.filter((segment: any) => segment);
      return validSegments.join(' -> ');
    }

    return (
      <List
        disablePadding
        sx={{
          height: 650,
          marginBottom: -7
        }}>
        {data?.map((item: any, index: any) => {
          const {ID, App, MRN, SubModule, Level2, FieldName, FieldValue} = item;
          const {Level1IsBH, Level2IsBH} = item;
          const uniqueIdentifier = `${ID} | ${App}:`;
          const fullPath = createPath(
            uniqueIdentifier,
            SubModule,
            Level2,
            FieldName
          );
          const path = createPath(SubModule, Level2, FieldName);
          const partsTitle = parse(FieldValue, match(FieldValue, searchQuery));
          const partsPath = parse(fullPath, match(fullPath, searchQuery));
          const mrnLabel = `MRN: ${MRN}`;
          let isRestricteddata: boolean;
          if (localStorage.getItem('check_isbh')) {
            if (!isRestricted) {
              let Level1IsBHvals, Level2IsBHvals;
              if (Level2IsBH === null || Level2IsBH === false) {
                Level2IsBHvals = true;
              }
              if (Level1IsBH === null || Level1IsBH === false) {
                Level1IsBHvals = true;
              }
              if (Level1IsBH === true) {
                Level1IsBHvals = false;
              }
              if (Level2IsBH === true) {
                Level2IsBHvals = false;
              }
              isRestricteddata = !(Level1IsBHvals && Level2IsBHvals);
            } else {
              isRestricteddata = false;
            }
          } else {
            isRestricteddata = false;
          }
          return (
            <ResultItem
              key={index}
              path={partsPath}
              title={partsTitle}
              groupLabel={searchQuery && mrnLabel}
              isBhvalue={isRestricteddata}
              checkisbhdata={item}
              onClickItem={() =>
                onDiscreteChange(path, item, isRestricteddata, searchQuery)
              }
            />
          );
        })}
      </List>
    );
  };

  const renderDocumentItems = () => {
    const data = documentsData.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );

    return (
      <List
        disablePadding
        sx={{
          height: 650,
          marginBottom: -7
        }}>
        {data?.map((item: any, index: any) => {
          const {
            metadata_storage_name: pathName,
            metadata_storage_path: fullFilePath,
            metatag5: metaData,
            content,
            merged_content: mergedContent,
            legacyapp,
            isbh: check_isbhrestriction
          } = item;

          // Remove base URL and first level directory from filePath
          const filePath = fullFilePath?.replace(
            /^https:\/\/[^/]+\/[^/]+\//,
            ''
          );

          let displayContent =
            mergedContent || content || 'No content available';
          // Replace all occurrences of '\n' with spaces
          displayContent = displayContent.replace(/\n/g, ' ');

          const truncatedContent =
            displayContent.length > 0
              ? displayContent.substring(0, 100) +
                (displayContent.length > 100 ? '...' : '')
              : 'No content available';

          // Check if the content is just whitespace or empty
          const isEmptyContent = /^\s*$/.test(displayContent);
          const fileType = pathName?.split('.').pop().toLowerCase();
          let isRestricteddata: boolean;
          if (localStorage.getItem('check_isbh')) {
            isRestricteddata = isRestricted
              ? false
              : !!(
                  Number(check_isbhrestriction) ??
                  Boolean(check_isbhrestriction)
                );
          } else {
            isRestricteddata = false;
          }
          return (
            <ResultItem
              key={index}
              title={[
                {
                  text: isEmptyContent
                    ? 'No content available'
                    : truncatedContent,
                  highlight: false
                }
              ]}
              path={[{text: filePath, highlight: false}]}
              legacyApp={legacyapp}
              metaData={metaData}
              isGroupLabelIcon={true}
              fileType={fileType}
              isBhvalue={isRestricteddata}
              checkisbhdata={item}
              onClickItem={() =>
                onDocChange(NavigateTo, filePath, searchQuery, item)
              }
            />
          );
        })}
      </List>
    );
  };

  const renderGlobalSearch = () => {
    const filteredDropDownValues = enableAISearch
      ? dropdownValues
      : dropdownValues.filter(item => item.label !== 'Documents');

    // Function to determine the group for each item
    const getGroup = (option: any) =>
      option.label === 'Documents' ? 'AI' : 'Discrete';

    return (
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row'
        }}
        spacing={1}>
        {showDropdown && (
          <RHFAutocomplete
            name={FormConstants.GLOBAL_SEARCH.LOCAL_SEARCH}
            autoHighlight
            fullWidth
            size="small"
            label={t('PRIVATE.GLOBAL_SEARCH.SELECT_SUBMODULE_SEARCH', {
              ns: 'private'
            })}
            placeholder={t('PRIVATE.GLOBAL_SEARCH.SELECT_SUBMODULE_SEARCH', {
              ns: 'private'
            })}
            value={autocompleteValue}
            multiple
            limitTags={1}
            groupBy={option => getGroup(option)}
            options={filteredDropDownValues}
            getOptionLabel={(option: any) => option.label}
            disableCloseOnSelect
            renderOption={(props, option) => (
              <li {...props} key={option.label}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedLabels.includes(option.label)}
                      onChange={() => handleLabelToggle(option.label)}
                    />
                  }
                  label={option.label}
                  sx={{
                    mx: 1
                  }}
                  onClick={event => {
                    event.preventDefault();
                    handleLabelToggle(option.label);
                  }}
                />
              </li>
            )}
            onChange={(event, newValue: any) => {
              setAutocompleteValue(newValue);
              if (!newValue) {
                setSelectedLabels([]);
              } else {
                setSelectedLabels(newValue.map((item: any) => item.label));
              }
            }}
            renderTags={selected => {
              if (selected.length === 0) return null;

              const firstLabel = selected[0]?.label || '';
              const remainingCount = selected.length - 1;

              return [
                <Chip
                  key={firstLabel}
                  size="small"
                  sx={{
                    height: '20px',
                    width: '110px',
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                    padding: '0 4px'
                  }}
                  label={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%'
                      }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          maxWidth: '70px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                        {firstLabel}
                      </Typography>
                      {remainingCount > 0 && (
                        <Typography variant="body2" sx={{marginLeft: '4px'}}>
                          +{remainingCount}
                        </Typography>
                      )}
                    </div>
                  }
                />
              ];
            }}
            sx={{
              minWidth: {xs: '100%', sm: '185px', md: '210px'},
              maxWidth: '400px',
              width: 230
            }}
          />
        )}

        <InputBase
          fullWidth
          autoFocus={false}
          placeholder={
            dropdownValues?.length > 0 || !showDropdown
              ? 'Global Search...'
              : 'Initialization...'
          }
          value={searchQuery}
          onChange={handleChange}
          inputProps={{
            sx: {
              mx: 2
            }
          }}
          sx={{
            border: searchQuery ? '1px solid black' : '1px solid #bab8b8',
            borderRadius: '8px',
            width: 230
          }}
        />
        {(parsedLocalSearch?.is_local_search ||
          parsedAiBlobSearch?.is_ai_blob_search ||
          (searchQuery && searchQuery.length > 0)) && (
          <LoadingButton
            type="submit"
            variant="outlined"
            onClick={() => {
              // if (!searchQuery) return;
              setSearchQuery('');
              setSelectedLabels([]);
              setAutocompleteValue([]);
              setSearchData([]);
              setDocumentsData([]);
              setToastPath('');
              setPrevSearchQuery('');
              setPrevDocSearchQuery('');
              setPrevSelectedLabels([]);
              setPrevDocSelectedLabels([]);
              sessionStorage.removeItem('@searchQuery');
              sessionStorage.removeItem('@toastPath');

              if (
                !parsedLocalSearch?.is_local_search &&
                !parsedAiBlobSearch?.is_ai_blob_search
              ) {
                return;
              }
              const updatedParams = EncryptUtils.setEncryptedParams({
                [BASE_RECORD_ID]: recordId,
                [BASE_LEGACY_APP]: legacyApp,
                [BASE_UNIQUE_ID]: uniqueId,
                [IS_FROM_EPIC]: isFromEpic,
                [IS_FROM_ATHENA]: isFromAthena,
                [MODE]: mode,
                [BASE_SPECIMEN_NUMBER]: specimenNumber,
                [BASE_SPECIMEN_NUMBER_ID]: specimenNumberId,
                [BASE_ACCESSION_NUMBER]:accessionNumber,
                [BASE_SPECIMEN_TYPE]: specimenType,
                [SEARCH_CONTEXT]: searchQuery
              });

              let url;

              if (moduleLocation === ModuleUrl) {
                url = `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${module_name}`;
              } else {
                const to =
                  module_name === FormConstants.MODULE.RIS
                    ? sub_module
                    : parsedLocalSearch?.submodule ?? 'documents';

                url = `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${module_name}/${BASE_ROUTE_SUB_MODULE}/${to}?${PARAMS_KEY}=${updatedParams}`;
                if (menuItem) {
                  url += `&${BASE_MENU_ITEM}=${menuItem}`;
                }
              }
              navigate(url);
            }}
            sx={{
              backgroundColor: theme.palette.primary.dark,
              color: 'white'
            }}>
            {t('PRIVATE.COMMON.CANCEL', {ns: 'private'})}
          </LoadingButton>
        )}
        {searchQuery && searchQuery.length > 0 && (
          <LoadingButton
            type="submit"
            variant="contained"
            onClick={() => {
              if (value === 0) {
                handleSearch();
              } else if (value === 1) {
                handleDocSearch();
              }
            }}>
            {t('PRIVATE.COMMON.SEARCH', {ns: 'private'})}
          </LoadingButton>
        )}
        {toastPath && parsedLocalSearch?.is_local_search && (
          <LoadingButton
            type="submit"
            variant="outlined"
            onClick={() => handleToastPath()}>
            {'Path'}
          </LoadingButton>
        )}
      </Stack>
    );
  };

  if (!globalSearchConfig || (!enableDiscreteSearch && !enableAISearch)) {
    return <></>;
  }

  return (
    <Stack
      spacing={1}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '38px',
        marginRight: 1
      }}>
      {renderGlobalSearch()}

      {/* Modal to view all search results */}
      <Dialog
        fullWidth
        maxWidth="lg"
        open={openModal.value}
        onClose={handleClose}
        transitionDuration={{
          enter: theme.transitions.duration.shortest,
          exit: 0
        }}
        PaperProps={{
          sx: {
            mt: 9,
            overflow: 'unset'
          }
        }}
        sx={{
          [`& .${dialogClasses.container}`]: {
            alignItems: 'flex-start'
          }
        }}>
        <Box sx={{p: 3, borderBottom: `solid 1px ${theme.palette.divider}`}}>
          <InputBase
            fullWidth
            autoFocus
            placeholder="Search..."
            value={searchQuery}
            onChange={handleChange}
            startAdornment={
              <InputAdornment position="start">
                <Iconify
                  icon="eva:search-fill"
                  width={24}
                  sx={{color: 'text.disabled'}}
                />
              </InputAdornment>
            }
            endAdornment={
              <Stack
                spacing={1}
                sx={{
                  display: 'flex',
                  flexDirection: 'row'
                }}>
                {searchQuery && searchQuery.length > 0 && (
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    onClick={() => {
                      value === 0
                        ? handleSearch(true)
                        : shouldCallAISearch && handleDocSearch(true);
                    }}>
                    {t('PRIVATE.COMMON.SEARCH', {ns: 'private'})}
                  </LoadingButton>
                )}

                <LoadingButton
                  type="submit"
                  variant="outlined"
                  onClick={() => {
                    openModal.onToggle();
                  }}
                  sx={{
                    backgroundColor: theme.palette.primary.light
                  }}>
                  {t('PRIVATE.COMMON.CLOSE', {ns: 'private'})}
                </LoadingButton>
              </Stack>
            }
            inputProps={{
              sx: {typography: 'h6'}
            }}
          />
        </Box>

        <Box sx={{borderBottom: 1, borderColor: 'divider', pl: 3}}>
          <Tabs
            value={value}
            onChange={(event, newValue) => {
              setValue(newValue);
              if (newValue === 0) {
                handleSearch(true);
              } else if (newValue === 1) {
                handleDocSearch(true);
              }
            }}
            aria-label="global_search">
            {enableDiscreteSearch && <Tab label="Discrete" value={0} />}
            {enableAISearch && <Tab label="Documents" value={1} />}
          </Tabs>
        </Box>
        {isLoading ? (
          <Stack
            sx={{
              p: 3,
              pt: 2,
              height: 650,
              justifyContent: 'center',
              alignItems: 'center'
            }}>
            <PulseLoader size={12} color={theme.palette.primary.main} />
          </Stack>
        ) : (
          <>
            <Scrollbar
              sx={{
                p: 3,
                pt: 2,
                height: 650
              }}>
              {value === 0 ? (
                notFound ? (
                  <SearchNotFound query={searchQuery} sx={{py: 10}} />
                ) : (
                  renderNonDocumentItems()
                )
              ) : noDoc ? (
                <SearchNotFound
                  select={
                    "To get documents, you must select 'Documents' in the multi-select dropdown."
                  }
                  sx={{py: 10}}
                />
              ) : docNotFound ? (
                <SearchNotFound query={searchQuery} sx={{py: 10}} />
              ) : (
                renderDocumentItems()
              )}
            </Scrollbar>
            <Box
              display="flex"
              justifyContent={
                localStorage.getItem('check_isbh')
                  ? 'space-between'
                  : 'flex-end'
              }>
              {localStorage.getItem('check_isbh') &&
              localStorage.getItem('check_isbh') ===
                'the view restricted data is false' ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    my: 2,
                    pl: 3
                  }}>
                  <Typography variant="body1">
                    {t('PRIVATE.IS_BH.VIEW_RESTRICTED_DATA', {ns: 'private'})}:
                  </Typography>
                  <Typography
                    variant="body1"
                    style={{
                      marginLeft: '4px',
                      marginRight: '8px'
                    }}>
                    N.A.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    my: 2,
                    pl: 3
                  }}>
                  <Typography variant="body1">
                    {t('PRIVATE.IS_BH.VIEW_RESTRICTED_DATA', {ns: 'private'})}:
                  </Typography>
                  <Typography
                    variant="body1"
                    style={{
                      color: isRestricted ? 'green' : 'red',
                      marginRight: '8px'
                    }}>
                    {isRestricted ? 'On' : 'Off'}
                  </Typography>
                </Box>
              )}
              {value === 0 && !notFound && totalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    my: 2,
                    mr: 4
                  }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handleChangePage}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
              {value === 1 && !docNotFound && docTotalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    my: 2,
                    mr: 4
                  }}>
                  <Pagination
                    count={docTotalPages}
                    page={currentPage}
                    onChange={handleChangePage}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Box>
          </>
        )}
      </Dialog>
    </Stack>
  );
}

export default memo(Searchbar);

